import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch } from '@/app/store/hooks';
import { fetchAgreementByQuote } from '@features/agreements';
import { getQuote } from '../services/quotes';
import { setCurrentStep, setStatus } from '../store/quoteSlice';

export type AgreementActivation =
    | { state: 'idle' }
    | { state: 'verifying' }
    | { state: 'active'; agreementRef: string | null }
    | { state: 'completed'; agreementRef: string | null }
    | { state: 'timeout'; checkAgain: () => void }
    | { state: 'finalisingTimeout'; agreementRef: string | null; checkAgain: () => void }
    | { state: 'forbidden' }
    | { state: 'unexpected'; status: string };

export const ACTIVATION_INTERVAL_MS = 5_000;
/** ~2 minutes: elemis delivers via Temporal with backoff, "2xx within 30s". */
export const ACTIVATION_MAX_ATTEMPTS = 24;
/** ~1 minute for sub-plan 4b's consumer to complete the quote behind the Agreement. */
export const COMPLETION_MAX_ATTEMPTS = 12;

function useBoundedPoll<T>(
    key: readonly unknown[],
    queryFn: () => Promise<T>,
    enabled: boolean,
    maxAttempts: number,
    isSettled: (data: T) => boolean,
    isFatal: (error: unknown) => boolean,
) {
    const [attempts, setAttempts] = useState(0);
    const [stopped, setStopped] = useState(false);
    const settledAtRef = useRef(0);

    // A fresh window every time polling is switched on again (a Retry after a
    // stopped or exhausted poll must not inherit its predecessor's stop).
    const wasEnabledRef = useRef(false);
    useEffect(() => {
        if (enabled && !wasEnabledRef.current) {
            setAttempts(0);
            setStopped(false);
            settledAtRef.current = 0;
        }
        wasEnabledRef.current = enabled;
    }, [enabled]);

    const query = useQuery({
        queryKey: key,
        queryFn,
        enabled: enabled && !stopped && attempts < maxAttempts,
        retry: false,
        refetchInterval: ACTIVATION_INTERVAL_MS,
        refetchOnWindowFocus: false,
    });

    const settledAt = Math.max(query.dataUpdatedAt, query.errorUpdatedAt);
    useEffect(() => {
        if (settledAt === 0 || settledAt === settledAtRef.current) return;
        settledAtRef.current = settledAt;
        setAttempts((prev) => prev + 1);
        if ((query.isSuccess && isSettled(query.data)) || (query.isError && isFatal(query.error))) {
            setStopped(true);
        }
    }, [settledAt, query.isSuccess, query.isError, query.data, query.error, isSettled, isFatal]);

    const checkAgain = useCallback(() => {
        setAttempts(0);
        setStopped(false);
    }, []);

    return { query, attempts, checkAgain };
}

const isForbidden = (error: unknown): boolean =>
    typeof error === 'object' && error !== null && (error as { status?: number }).status === 403;
const never = () => false;

/** Phase 1 polls the Agreement list by quote id until the webhook has flipped it draft -> active;
 *  phase 2 polls the quote, then mirrors the server's own status into Redux. */
export function useAgreementActivation(
    agreementId: string | null,
    quoteId: string | undefined,
    enabled: boolean,
): AgreementActivation {
    const dispatch = useAppDispatch();

    // The list is not behind the access justification gate, so a sale never prompts.
    const agreement = useBoundedPoll(
        ['elemis-checkout', 'agreement-activation', quoteId],
        () => fetchAgreementByQuote(quoteId!, { suppressGlobalForbiddenToast: true }),
        enabled && agreementId !== null && !!quoteId,
        ACTIVATION_MAX_ATTEMPTS,
        useCallback((data: { status: string } | null) => data !== null && data.status !== 'draft', []),
        isForbidden,
    );
    const agreementStatus = agreement.query.data?.status;
    const isActive = agreementStatus === 'active';
    // The ref the verification read returned, not the one initiation reported.
    const confirmedRef = agreement.query.data?.agreementRef || null;

    const quote = useBoundedPoll(
        ['elemis-checkout', 'quote-completion', quoteId],
        () => getQuote(quoteId!),
        enabled && isActive && !!quoteId,
        COMPLETION_MAX_ATTEMPTS,
        useCallback((data: { status?: string }) => data.status === 'completed', []),
        never,
    );
    const quoteCompleted = quote.query.data?.status === 'completed';

    // The wizard's terminal UI is the summary step's: completion can land while
    // the user has navigated back, so mirror the step as well as the status.
    useEffect(() => {
        if (!quoteCompleted) return;
        dispatch(setStatus('completed'));
        dispatch(setCurrentStep('summary'));
    }, [quoteCompleted, dispatch]);

    if (!enabled || agreementId === null) return { state: 'idle' };
    if (agreement.query.isError && isForbidden(agreement.query.error)) return { state: 'forbidden' };
    if (agreementStatus !== undefined && agreementStatus !== 'draft' && !isActive) {
        return { state: 'unexpected', status: agreementStatus };
    }
    if (quoteCompleted) return { state: 'completed', agreementRef: confirmedRef };
    if (isActive) {
        // Phase two exhausting is not phase one exhausting: the payment IS
        // confirmed by here, and only the quote's finalisation is outstanding.
        return quote.attempts >= COMPLETION_MAX_ATTEMPTS
            ? { state: 'finalisingTimeout', agreementRef: confirmedRef, checkAgain: quote.checkAgain }
            : { state: 'active', agreementRef: confirmedRef };
    }
    if (agreement.attempts >= ACTIVATION_MAX_ATTEMPTS) {
        return { state: 'timeout', checkAgain: agreement.checkAgain };
    }
    return { state: 'verifying' };
}
